import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import HeaderComponent from "../components/HeaderComponent";
import SiteFooter from "../components/SiteFooter";
import { getProjectBySlug, navLinks } from "../data/siteContent";

function ProjectDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const project = getProjectBySlug(slug);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  useEffect(() => {
    document.body.classList.add("project-page");

    return () => {
      document.body.classList.remove("project-page");
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      document.body.classList.toggle("scrolled", window.scrollY > 100);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.body.classList.remove("scrolled");
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("mobile-nav-active", isMobileNavOpen);

    return () => {
      document.body.classList.remove("mobile-nav-active");
    };
  }, [isMobileNavOpen]);

  const handleNavClick = (sectionId) => {
    setIsMobileNavOpen(false);
    navigate({ pathname: "/", hash: `#${sectionId}` });
  };

  const toggleMobileNav = () => {
    setIsMobileNavOpen((currentValue) => !currentValue);
  };

  if (!project) {
    return (
      <>
        <HeaderComponent
          activeSection=""
          isMobileNavOpen={isMobileNavOpen}
          navLinks={navLinks}
          onNavigate={handleNavClick}
          onToggleMobileNav={toggleMobileNav}
          getSectionHref={(sectionId) => `/#${sectionId}`}
        />
        <section className="section project-detail-shell">
          <div className="container project-detail-container">
            <Link to="/" className="project-back-link">
              <i className="bi bi-arrow-left me-2"></i>
              <span>Back to home</span>
            </Link>

            <div className="project-not-found text-center">
              <span className="project-status-pill">Project not found</span>
              <h1 className="mt-3 mb-3">This project page is not available.</h1>
              <p className="mb-4">
                The project you are looking for may have moved or is not yet published on the website.
              </p>
              <Link to="/" className="btn-get-started">
                <i className="bi bi-house me-2"></i>
                Return to homepage
              </Link>
            </div>
          </div>
        </section>
        <SiteFooter />
      </>
    );
  }

  return (
    <>
      <HeaderComponent
        activeSection=""
        isMobileNavOpen={isMobileNavOpen}
        navLinks={navLinks}
        onNavigate={handleNavClick}
        onToggleMobileNav={toggleMobileNav}
        getSectionHref={(sectionId) => `/#${sectionId}`}
      />
      <section className="section project-detail-shell">
        <div className="container project-detail-container">
          {/* Back Link */}
          <Link to="/" className="project-back-link d-inline-flex align-items-center mb-4">
            <i className="bi bi-arrow-left me-2"></i>
            <span>Back to home</span>
          </Link>

          {/* Hero Section - Improved layout */}
          <div className="row gy-5 align-items-center project-detail-hero mb-5">
            <div className="col-lg-6">
              <span className="project-status-pill d-inline-block mb-3">{project.status}</span>
              <h1 className="display-5 fw-bold mb-3">{project.name}</h1>
              <p className="project-detail-lead fs-5 mb-3">{project.headline}</p>
              <p className="text-muted mb-4">{project.overview}</p>

              <div className="project-detail-actions">
                <a href="#project-contact" className="btn-get-started">
                  <i className="bi bi-chat-dots me-2"></i>
                  {project.cta}
                </a>
                {project.liveUrl ? (
                  <a
                    href={project.liveUrl}
                    className="btn-watch-video d-inline-flex align-items-center"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <i className="bi bi-box-arrow-up-right me-2"></i>
                    <span>Visit live product</span>
                  </a>
                ) : null}
              </div>
            </div>

            <div className="col-lg-6">
              <div className="project-detail-image-wrap project-detail-media">
                <img
                  src={`/${project.image}`}
                  alt={project.name}
                  className="img-fluid project-detail-image w-100"
                />
              </div>
            </div>
          </div>

          {/* Challenge & Solution Section - Better visual separation */}
          <div className="row gy-4 project-detail-sections mb-4">
            <div className="col-lg-6">
              <article className="project-detail-card h-100">
                <div className="project-detail-card-header">
                  <div className="service-offering-icon project-detail-card-icon">
                    <i className="bi bi-exclamation-triangle fs-4"></i>
                  </div>
                  <h2 className="mb-0 fs-3">The Opportunity</h2>
                </div>
                <p className="project-detail-copy">{project.challenge}</p>
              </article>
            </div>

            <div className="col-lg-6">
              <article className="project-detail-card h-100">
                <div className="project-detail-card-header">
                  <div className="service-offering-icon project-detail-card-icon">
                    <i className="bi bi-lightbulb fs-4"></i>
                  </div>
                  <h2 className="mb-0 fs-3">Our Approach</h2>
                </div>
                <p className="project-detail-copy">{project.solution}</p>
              </article>
            </div>
          </div>

          {/* Capabilities & Outcomes Section - Enhanced lists */}
          <div className="row gy-4 project-detail-sections mb-4">
            <div className="col-lg-6">
              <article className="project-detail-card h-100">
                <h2 className="fs-3 mb-3">What This Work Involves</h2>
                <ul className="project-detail-list">
                  {project.capabilities.map((item) => (
                    <li key={item} className="d-flex align-items-start mb-2">
                      <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            </div>

            <div className="col-lg-6">
              <article className="project-detail-card h-100">
                <h2 className="fs-3 mb-3">Current Value</h2>
                <ul className="project-detail-list">
                  {project.outcomes.map((item) => (
                    <li key={item} className="d-flex align-items-start mb-2">
                      <i className="bi bi-stars text-primary me-2 mt-1"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            </div>
          </div>

          {/* CTA Section - Improved design */}
          <section id="project-contact" className="project-detail-card project-contact-card mb-5">
            <div className="text-center py-4">
              <div className="service-offering-icon project-contact-icon mx-auto mb-3">
                <i className="bi bi-envelope-paper fs-2"></i>
              </div>
              <h2 className="mb-3">Interested in a similar project?</h2>
              <p className="project-contact-copy mb-4 mx-auto">
                If you want a closer look at how we work or you are planning something related,
                let's talk through the idea and what the next practical step should be.
              </p>
              <div className="project-detail-actions justify-content-center">
                <a href="/#contact" className="btn-get-started">
                  <i className="bi bi-chat-dots me-2"></i>
                  Contact Operating Software
                </a>
              </div>
            </div>
          </section>
        </div>
      </section>
      <SiteFooter />
    </>
  );
}

export default ProjectDetailPage;
